import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import { useNavigate } from 'react-router-dom';
import { Modal, Button, Spin } from 'antd';
import axios from 'axios';
import { ActionCardInsidewig } from '../Atoms/ActionCardInsidewig';
import Global from '../../assets/images/RewardsImages/GlobalAwards.svg';
import { useTranslation } from 'react-i18next';

interface RewardsProps {
  visible: boolean;
  onClose: () => void;
  userRoles: string[];
}

interface RewardCategory {
  rewardName: string;
  rolesList?: string[];
  icon?: string;
  [key: string]: any;
}

// Cache configuration
// Strategy: Always fetch fresh data from API on modal open
// No caching to ensure users always see the latest rewards
const CACHE_CONFIG = {
  REWARD_CATEGORIES_EXPIRY: 15 * 60 * 1000, // 15 minutes
  REQUEST_TIMEOUT: 10000, // 10 seconds
};

const Rewards: React.FC<RewardsProps> = ({ visible, onClose, userRoles }) => {
  const [selectedCard, setSelectedCard] = useState<string | null>(null);
  const [nominatorName, setNominatorName] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [cards, setCards] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  const { instance, accounts } = useMsal();
  const [nominatorEmail, setNominatorEmail] = useState('');
  const navigate = useNavigate();

  const fetchToken = async () => {
    const res = await instance.acquireTokenSilent({
      ...loginRequest,
      account: accounts[0]
    });
    return res.idToken;
  };

  const fetchRewardCategories = async (): Promise<RewardCategory[]> => {
    try {
      const token = await fetchToken();
      const baseURL = process.env.REACT_APP_API_URL;
      const response = await axios.get(`${baseURL}/api/Rewards/ParentNull`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        timeout: CACHE_CONFIG.REQUEST_TIMEOUT,
      });

      if (response.status === 200) {
        console.log('[Rewards] Fetched fresh data from API');
        return response.data;
      }
    } catch (error) {
      console.error('Error fetching reward categories:', error);
    }
    return [];
  };

  // Use localStorage data directly instead of making redundant API calls
  useEffect(() => {
    const userMail = localStorage.getItem('UserMail');
    const userId = localStorage.getItem('UserId');
    
    if (userMail) {
      setNominatorEmail(userMail.toLowerCase());
    }
  }, []);

  useEffect(() => {
    const loadRewardCategories = async () => {
      if (!visible) return;
      
      // Only fetch if cards are empty
      if (cards.length > 0) return;
      
      setLoading(true);
      const startTime = performance.now();
      
      try {
        const rewardCategories = await fetchRewardCategories();
        
        if (rewardCategories && rewardCategories.length > 0) {
          const mappedCards = rewardCategories
            .filter((category: RewardCategory) =>
              category.rolesList?.some((role) =>
                userRoles.some((userRole) =>
                  userRole.replace(/\s+/g, '').toLowerCase() === role.replace(/\s+/g, '').toLowerCase()
                )
              )
            )
            .map((category: RewardCategory) => ({
              icon: (
                <div className="w-20 h-20 md:w-24 md:h-24 flex items-center justify-center">
                  <img
                    src={
                      category.icon
                        ? `data:image/svg+xml;base64,${category.icon}`
                        : Global
                    }
                    alt={category.rewardName}
                    className="w-8 h-8 object-contain"
                  />
                </div>
              ),
              title: category.rewardName,
              details: category,
            }));

          setCards(mappedCards);
          console.log(`[Rewards] Loaded ${mappedCards.length} cards in ${(performance.now() - startTime).toFixed(2)}ms`);
        } else {
          console.warn('[Rewards] No reward categories found');
          setCards([]);
        }
      } catch (error) {
        console.error('[Rewards] Error loading reward categories:', error);
        setCards([]);
      } finally {
        setLoading(false);
      }
    };

    // Load data when modal becomes visible
    if (visible) {
      loadRewardCategories();
    }
  }, [visible, userRoles]);

  const handleCardClick = (title: string) => {
    // console.log('Card clicked:', title); // Debugging
    setHasUnsavedChanges(true);
    setSelectedCard(title);
  };

  const handleNextStep = async () => {
    if (selectedCard) {
      const selectedReward = cards.find((card) => card.title === selectedCard);
      if (selectedReward) {
        try {
          const token = await fetchToken();
          const baseURL = process.env.REACT_APP_API_URL;
          const response = await axios.get(
            `${baseURL}/api/Rewards/ByParentId/${selectedReward.details.rewardTypeId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (response.status === 200) {
            const childRewards = response.data;

            // Check if any childReward has regionName including 'global'
            const hasGlobalRole = childRewards.some(
              (reward: any) =>
                Array.isArray(reward.regionName) &&
                reward.regionName.includes('global')
            );

            let updatedUserRoles = [...userRoles];

            if (hasGlobalRole) {
              const managerId = localStorage.getItem('UserId');
              if (!managerId) {
                return
              }

              const isManagerRes = await axios.get(
                `${baseURL}/api/Users/IsPeopleManagerGlobal/${managerId}`,
                {
                  headers: {
                    Authorization: `Bearer ${token}`,
                  },
                }
              );
              if (
                isManagerRes.status === 200 &&
                isManagerRes.data === true &&
                !updatedUserRoles.includes('peoplemanager')
              ) {
                updatedUserRoles.push('peoplemanager');
              }
            }

            const { icon, ...serializableParentReward } = selectedReward;

            navigate('/awards-page', {
              state: {
                rewardDetails: childRewards,
                parentReward: serializableParentReward,
                userDetails: updatedUserRoles,
              },
            });
          }
        } catch (error) {
          console.error('Error fetching child rewards:', error);
        }
      }
    }
  };

  useEffect(() => {
    if (selectedCard) {
      // console.log('Selected card:', selectedCard); // Debugging
      handleNextStep();
    }
  }, [selectedCard]);

  const resetForm = () => {
    setSelectedCard(null);
    setHasUnsavedChanges(false);
    // Don't clear cards - keep them for next open
  };

  const handleModalClose = () => {
    if (hasUnsavedChanges) {
      Modal.confirm({
        title: 'Unsaved Changes',
        content: 'You have unsaved changes. Are you sure you want to close the form? Your changes will be lost.',
        onOk: () => {
          resetForm();
          onClose();
        },
      });
    } else {
      resetForm();
      onClose();
    }
  };

  const renderCardContent = () => (
    <div style={{ height: 'auto', minHeight: '200px' }}>
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '60px 0' }}>
          <Spin size="large" tip="Loading rewards..." />
        </div>
      ) : cards.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#666' }}>
          No rewards available
        </div>
      ) : (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          flexWrap: 'wrap',
          gap: '32px',
          padding: '40px 0 20px'
        }}>
          {cards.map((card, index) => (
          <div
            key={index}
            onClick={() => handleCardClick(card.title)}
            style={{
              position: 'relative',
              cursor: 'pointer',
              width: '185px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            <div style={{
              position: 'relative',
              width: '185px',
              height: '185px',
              backgroundColor: selectedCard === card.title ? '#F3E8FF' : '#B3E5FC',
              borderRadius: '18px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              border: selectedCard === card.title ? '4px solid rgb(105, 57, 218)' : '1px solid #87DEEB',
              marginBottom: '16px'
            }}>
              <ActionCardInsidewig
                icon={card.icon}
                title={card.title}
                message={''}
                background={'#68C4D8 '}
                border={'1px solid #68C4D8'}
                variant="default"
              />
            </div>
          </div>
        ))}
        </div>
      )}
    </div>
  );

  return (
    <Modal
      title={
        <div style={{
          fontSize: '20px',
          fontWeight: 600,
          color: '#111827',
          padding: '8px 0'
        }}>
          {t('rewards')}
        </div>
      }
      visible={visible}
      onCancel={handleModalClose}
      footer={null}
      centered
      width={750}
      bodyStyle={{ padding: '24px 32px 10px' }}
    >
      {renderCardContent()}
    </Modal>
  );
};

export default Rewards;